/** Map Supabase Auth mail errors to actionable copy for signup / resend / reset. */
export function mapAuthEmailError(message: string): string {
  const m = message.trim();
  const low = m.toLowerCase();

  if (
    low.includes('service currently unavailable due to hook') ||
    low.includes('currently unavailable due to hook')
  ) {
    return (
      'Auth email service is down (hook unavailable). The auth-send-email Edge Function may be failing to start — ' +
      'redeploy with npm run supabase:auth-email:deploy, then open …/auth-send-email?diagnostics=1 (should return JSON, not 503). ' +
      'If diagnostics are OK, re-sync SEND_EMAIL_HOOK_SECRET from Dashboard → Auth Hooks → Send Email.'
    );
  }

  if (low.includes('unexpected status code returned from hook: 500') || low.includes('hook: 500')) {
    return (
      'The auth email hook failed (500). Open Supabase → Edge Functions → auth-send-email → Logs. ' +
      'Usually: (1) SEND_EMAIL_HOOK_SECRET does not match the secret shown under Auth Hooks → Send Email — re-copy and run ' +
      'npx supabase secrets set SEND_EMAIL_HOOK_SECRET="v1,whsec_..." then npm run supabase:auth-email:deploy, or ' +
      '(2) Resend has not verified joindistrict88.com for info@joindistrict88.com — verify the domain in resend.com/domains.'
    );
  }

  if (low.includes('unexpected status code returned from hook: 401') || low.includes('hook: 401')) {
    return (
      'Auth email hook rejected the request (invalid signature). In Dashboard → Auth Hooks → Send Email, copy the hook secret again, then run: ' +
      'npx supabase secrets set SEND_EMAIL_HOOK_SECRET="v1,whsec_..." and npm run supabase:auth-email:deploy'
    );
  }

  if (low.includes('unexpected status code returned from hook: 405') || low.includes('hook: 405')) {
    return (
      'Email hook returned 405 (method not allowed). Redeploy with npm run supabase:auth-email:deploy, then in Dashboard → Auth Hooks → Send Email, select the auth-send-email Edge Function (do not paste a GET-only URL).'
    );
  }

  if (
    low.includes('error sending confirmation email') ||
    low.includes('error sending recovery email') ||
    low.includes('error sending magic link email') ||
    low.includes('unable to send email') ||
    low.includes('smtp')
  ) {
    return (
      'We could not send that email. This usually means Custom SMTP in Supabase is misconfigured. ' +
      'Fix: Dashboard → Authentication → SMTP — verify Resend credentials and that joindistrict88.com is verified in Resend, ' +
      'or disable Custom SMTP temporarily, or enable the Send Email hook (see supabase/email-templates/README.md).'
    );
  }

  if (
    low.includes('email rate limit') ||
    low.includes('over_email_send_rate_limit') ||
    low.includes('over_email_send_rate') ||
    (low.includes('rate') && low.includes('email'))
  ) {
    return (
      'Email rate limit reached. Supabase caps how many signup, reset, and resend emails can be sent per hour, ' +
      'and enforces a short cooldown before the same address can get another message. ' +
      'Wait about 60 seconds if you just clicked Resend, or up to an hour if you have been testing repeatedly. ' +
      'To raise limits: Supabase Dashboard → Authentication → Rate Limits (increase “Email sent” and signup/reset intervals). ' +
      'With Resend + the auth-send-email hook, you can set a much higher hourly cap than the default built-in mailer.'
    );
  }

  if (low.includes('rate') || low.includes('too many') || low.includes('exceeded') || low.includes('429')) {
    return (
      'Too many email attempts. Supabase limits how often confirmation and reset emails can be sent. ' +
      'Wait 60 seconds before resending to the same address, or up to an hour if you have been testing repeatedly. ' +
      'Raise limits in Supabase Dashboard → Authentication → Rate Limits.'
    );
  }

  if (low.includes('redirect') || low.includes('url') && low.includes('allow')) {
    return (
      'Email redirect URL is not allowed. Add your site URL under Supabase → Authentication → URL Configuration ' +
      '(include http://localhost:3000/** for local dev).'
    );
  }

  return m;
}
